import { ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';
import logoSvg from '@/assets/logo.svg';

const Header = () => {
  const { totalItems, setIsCartOpen, ordersEnabled } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigateToSection = (id: string) => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        const headerHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  };

  const goHome = () => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button onClick={goHome} className="flex items-center gap-2">
            <img src={logoSvg} alt="Pizzeria La Torre" className="h-10 md:h-12 w-auto" />
            <span className="font-display text-xl md:text-2xl font-bold text-foreground">
              Pizzeria La Torre
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/menu" className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors">
              Menù
            </Link>
            <button onClick={() => navigateToSection('chi-siamo')} className="text-muted-foreground hover:text-primary transition-colors">
              Chi Siamo
            </button>
            <button onClick={() => navigateToSection('servizi')} className="text-muted-foreground hover:text-primary transition-colors">
              Servizi
            </button>
            <button onClick={() => navigateToSection('perche-noi')} className="text-muted-foreground hover:text-primary transition-colors">
              Perché Noi
            </button>
            <button onClick={() => navigateToSection('recensioni')} className="text-muted-foreground hover:text-primary transition-colors">
              Recensioni
            </button>
            <button onClick={() => navigateToSection('contatti')} className="text-muted-foreground hover:text-primary transition-colors">
              Contatti
            </button>
            <Link to="/aic" className="inline-flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors">
              <GlutenFreeIcon size={16} className="text-green-400" />
              <span>AIC</span>
            </Link>
          </nav>

          {/* Cart Button & Mobile Menu */}
          <div className="flex items-center gap-4">
            {ordersEnabled && (
              <Button
                variant="cart"
                size="icon"
                onClick={() => setIsCartOpen(true)}
                className="relative"
              >
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-scale-in">
                    {totalItems}
                  </span>
                )}
              </Button>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-border animate-fade-in bg-background">
            <div className="flex flex-col gap-4">
              <Link to="/menu" onClick={() => setMobileMenuOpen(false)} className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm w-fit">
                Menù
              </Link>
              <button onClick={() => navigateToSection('chi-siamo')} className="text-left text-muted-foreground hover:text-primary transition-colors py-2">
                Chi Siamo
              </button>
              <button onClick={() => navigateToSection('servizi')} className="text-left text-muted-foreground hover:text-primary transition-colors py-2">
                Servizi
              </button>
              <button onClick={() => navigateToSection('perche-noi')} className="text-left text-muted-foreground hover:text-primary transition-colors py-2">
                Perché Noi
              </button>
              <button onClick={() => navigateToSection('recensioni')} className="text-left text-muted-foreground hover:text-primary transition-colors py-2">
                Recensioni
              </button>
              <button onClick={() => navigateToSection('contatti')} className="text-left text-muted-foreground hover:text-primary transition-colors py-2">
                Contatti
              </button>
              <Link 
                to="/aic" 
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors py-2"
              >
                <GlutenFreeIcon size={16} className="text-green-400" />
                <span>AIC</span>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
